// Lesson 1.4, Make it sound like you.
// Approved by Leena 2026-10-09, converted mechanically from the draft.

export default {
  key: '1-4',
  title: 'Make it sound like you',
  minutes: 18,
  status: 'ready',
  outcome: 'A voice sample, a list of phrases to ban, and an editing pass.',

  why: 'People can tell. Clients can tell. The moment something you send reads like it came out of a machine, it costs you a small amount of credibility, and the second time it costs more. This lesson is about making the output sound like a person, specifically you, which also happens to be the difference between work you can send and work you have to rewrite.',

  blocks: [
    {
      type: 'concept',
      heading: 'Give it samples of what you write',
      body: [
        { type: 'rule', text: 'Find three things you have written that you were happy with, and paste them in. That is the whole technique.' },
        { type: 'p', text: 'An email to a client. A short update. Something you would be glad to have sent again. That set is your voice sample, and you will reuse it for years.' },
        { type: 'p', text: 'Telling it "be conversational" does almost nothing, because everyone’s conversational is different. Samples are not a description of how you write, they are the evidence.' },

        { type: 'sub', text: 'The phrases that give it away' },
        { type: 'p', text: 'There is a house style these tools drift into. Once you can see it you cannot unsee it, and you will catch it in your own drafts.' },
        { type: 'list', items: [
          'delve, leverage, utilise, robust, seamless, landscape, tapestry, realm',
          '"It is not just X, it is Y."',
          '"In today’s fast-paced world…"',
          '"I hope this email finds you well."',
          'Three-item lists where two would do, over and over',
          'An opening paragraph that restates the question before answering it',
          'Em dashes everywhere. A real tell, and worth banning outright.',
        ] },
        { type: 'p', text: 'Give it the list and tell it not to use any of them. It will comply, and the output immediately reads less synthetic.' },

        { type: 'sub', text: 'The editing pass is not optional' },
        { type: 'p', text: 'Never send the first draft. Read it out loud, or at least read it as if someone sent it to you. Cut the first sentence, which is almost always warm-up. Put one specific detail back in that only you would know, because specificity is what machines cannot fake. That is usually a sixty second job and it is the difference between fine and yours.' },
        { type: 'rule', text: 'Show it three things you wrote. Ban the tells. Always cut the first sentence.' },
      ],
    },

    {
      type: 'exercise',
      heading: 'Build your voice sample',
      steps: [
        { id: 'find_three', text: 'Find three pieces of your own writing you were happy with. Put them somewhere you can get at them, a note or a doc.' },
        { id: 'draft_with_samples', text: 'Take a real message you need to send this week. Ask for a draft, pasting your three samples in and telling it to match them, plus the banned phrases list.' },
        { id: 'editing_pass', text: 'Do the editing pass. Cut the opening sentence, add one specific detail, read it aloud. Tick the box when you have sent it.' },
      ],
    },

    {
      type: 'concept',
      body: [
        { type: 'callout', tone: 'info', text: 'Keep that voice sample. From here on, every prompt in this course gets better by pasting it in, and in the next lesson you will set it up once so you stop pasting it at all.' },
      ],
    },

    {
      type: 'quickcheck',
      questions: [
        {
          q: 'Which does more to make output sound like you?',
          options: ['Telling it "be conversational and friendly"', 'Pasting in three things you wrote'],
          answer: 1,
          because: 'Adjectives mean something different to everyone. Examples do not.',
        },
        {
          q: 'You have a draft that reads well but a bit generic. Fastest fix?',
          options: ['Ask for a longer version', 'Cut the first sentence and add one detail only you would know', 'Change the tone instruction and regenerate'],
          answer: 1,
          because: 'Specificity is the thing it cannot invent. Regenerating usually gives you a different flavour of generic.',
        },
      ],
    },

    {
      type: 'promptcard',
      title: 'Sound like me',
      body: 'Here are three things I wrote: [paste]. Match how I write: sentence length, how formal I am, how I open and close. Do not use any of these: delve, leverage, utilise, robust, seamless, landscape, tapestry, realm, "it is not just X it is Y", "in today’s fast-paced world", "I hope this email finds you well". No em dashes. Do not open by restating my question. Then write: [your task].',
    },

    {
      type: 'takeaway',
      text: 'Show it your writing, ban the tells, and never send the first draft.',
    },
  ],
};
